// helpContent.js — Contextual help for all ClearView CRM modules
// Location: src/constants/helpContent.js
//
// Usage in any view:
//   import { HELP } from "../../constants/helpContent";
//   <ZoneOneHeader ... helpContent={HELP.cultivation} />
//
// Schema (all fields optional except title):
//   title:     string  — panel title
//   intro:     string  — one-paragraph framing at the top of the panel
//   workflows: Workflow[] — see below; rendered on the Workflows tab
//   sections:  Section[]  — legacy alias for reference content (still supported)
//   reference: Section[]  — preferred name going forward (same shape as sections)
//   tip:       string  — pinned callout at the bottom of the panel
//
// Workflow {
//   id:           string  — unique within the view
//   title:        string  — imperative ("Triage your portfolio this week")
//   goal:         string  — one-sentence why; becomes the callout on the card
//   timeEstimate: string  — free-form ("20 min", "Monday morning")
//   steps: Step[]         — ordered instructions
//   done?:        string  — optional "you know you're done when…" confirmation
// }
//
// Step {
//   text: string          — imperative instruction, one sentence
//   cta?: {               — optional call-to-action button
//     kind: "nav",        — only "nav" supported today
//     view: string,       — target route (e.g. "capacity", "cultivation")
//     label: string,      — button label
//     openHelp?: boolean  — when true, arriving view re-opens its help panel
//   }
//   kbd?: string[]        — keyboard shortcut badge tokens (e.g. ["Ctrl","K","/log"])
// }
//
// Section {
//   heading: string
//   items:   string[]     — descriptive bullet copy (UI anatomy, reference)
// }
//
// Tone: prefer imperative. "Open the Capacity view" beats "You can open…".

// ──────────────────────────────────────────────────────────────
// SHARED SECTION — Quick Command Bar (Ctrl+K) keyboard shortcuts
// Spread into any HELP entry to surface the navigation shortcuts.
// ──────────────────────────────────────────────────────────────
const QUICK_COMMAND_BAR_SECTION = {
  heading: "Quick Command Bar — Ctrl+K (Cmd+K on Mac)",
  items: [
    "Press Ctrl+K (or Cmd+K) anywhere in the app — or click the ⌘K Quick button at the start of the toolbar — to open the command bar. The same bar handles three jobs: navigation, donor search, and quick task creation. Esc closes it.",
    "Slash navigation: type /h or /home for the dashboard, /d or /donors for the donors list, /g or /gifts for gifts, /c or /cultivation for the pipeline, /t or /tasks for the task view, /r or /reports for reports, /e or /events, /p or /portfolios, /s or /settings.",
    "Contact reports shortcut: /cr (or /contactreports) jumps straight to the new Reports → Contact Reports tab.",
    "Donor search: type /find Smith (or just 'find Smith' without the slash) to open the donors list pre-filtered to that name. Saves the trip through Donors → search box.",
    "Today's task drawer: /today opens the task drawer overlay without leaving your current view — useful for a quick check of what's due.",
    "Plain English also works for the most common commands: 'go home', 'open gifts', 'find Sarah'. The compass icon on the left of the bar lights up when the parser recognizes a navigation command — press Enter to navigate.",
    "Anything else you type creates a task. The bar parses dates, priorities, categories, and donor names from natural language: 'follow up with Sarah next Tuesday urgent' creates a high-priority task linked to Sarah due next Tuesday.",
    "Type /help (or /?) inside the bar to see the full command list inline.",
  ],
};

export const HELP = {
  // ──────────────────────────────────────
  // DASHBOARD VIEW
  // ──────────────────────────────────────
  dashboard: {
    title: "Dashboard",
    intro:
      "Your home screen. A living snapshot of organizational health — giving totals, engagement signals, and the Pollen view field showing where donor relationships are blooming right now.",
    workflows: [
      {
        id: "start-of-day",
        title: "Start your day here",
        goal: "Get oriented in under five minutes before you dive into the real work.",
        timeEstimate: "5 min, first thing",
        steps: [
          {
            text: "Check the My Day sidebar on the left — Tasks Due, Overdue Touches, Opportunity Gap. Anything red gets handled before the rest of the dashboard.",
          },
          {
            text: "Glance at the four giving KPIs at the top (Month / Quarter / Year / Active Donors). Any surprises?",
          },
          {
            text: "Scan the Pollen field for flowers blooming in unexpected places — a high, colorful flower on the far left is a highly engaged donor you haven't touched in a while.",
          },
          {
            text: "Check Recent Gifts and Recent Contacts for anything worth an immediate thank-you or follow-up.",
          },
          {
            text: "If something jumps out, press Ctrl+K and type the donor's name to open their profile.",
            kbd: ["Ctrl", "K"],
          },
        ],
        done: "You've acknowledged one recent gift or engagement signal before any inbox triage.",
      },
      {
        id: "respond-to-pollen",
        title: "Respond to a Pollen card prompt",
        goal: "Turn a dashboard signal into an actual donor touch.",
        timeEstimate: "10 min per card",
        steps: [
          {
            text: "Click any Pollen card header to open it full-width and see the list of donors it's surfacing.",
          },
          {
            text: "Click a donor to jump to their profile — the right email template auto-loads based on which card you came from (thank-you from Recent Gifts, renewal ask from Renewals, etc.).",
          },
          {
            text: "Press Ctrl+K, type /log, Enter. The Log Contact modal opens with the template already applied.",
            kbd: ["Ctrl", "K", "/log"],
          },
          {
            text: "Edit freely, save. The donor drops off the card's list (logging the outreach is the dismiss).",
          },
        ],
        done: "The donor is contacted, the contact log has the right purpose tag, and the card shows one fewer donor on it.",
      },
      {
        id: "global-search",
        title: "Find any donor in two seconds",
        goal: "Skip navigating to the Donors list when you already know who you're looking for.",
        timeEstimate: "5 seconds",
        steps: [
          {
            text: "Press Ctrl+K to open the command bar from anywhere.",
            kbd: ["Ctrl", "K"],
          },
          {
            text: "Start typing the donor's name, organization, email, or Donor ID.",
          },
          {
            text: "Press Enter (or click) on the result. You land on their profile, last tab you left it on.",
          },
        ],
      },
    ],
    reference: [
      QUICK_COMMAND_BAR_SECTION,
      {
        heading: "Global Search",
        items: [
          "The search bar in the toolbar lets you find any donor from the dashboard — type 2 or more characters to see matching results by name, organization, email, or Donor ID.",
          "Click a result to go directly to that donor's full detail record.",
          "This is the fastest way to get to a specific donor without navigating to the Donors list first.",
        ],
      },
      {
        heading: "Giving Snapshot",
        items: [
          "Four KPI tiles at the top: This Month, This Quarter, This Year, and Active Donors.",
          "These reflect real-time gift data from the database. Active Donors counts donors with at least one gift in the current fiscal year.",
        ],
      },
      {
        heading: "Pollen View — Who's Blooming?",
        items: [
          "The flower field is a visual map of your donor base. Each flower represents one donor. The field answers one question: where is the life?",
          "Position tells you two things at once. Left-to-right is recency — flowers on the right were contacted recently, flowers on the left haven't been heard from in a while. Bottom-to-top is engagement score — higher flowers have stronger engagement.",
          "Color shows pathway stage: gray = New Donor, blue = Donor of Record, green = Engagement, amber = Advocate.",
          "Bloom level shows engagement intensity. A small dot is a dormant donor (score of zero). A single sprout has minimal engagement. Full five-petal blooms are your strongest advocates.",
          "Size reflects the strength of the signal — more active contacts and higher scores produce larger flowers.",
        ],
      },
      {
        heading: "The Four Quadrants",
        items: [
          "Upper-right ('Go here first'): High engagement + recent contact. These donors are actively engaged AND being tended to. This is where relationships are thriving — keep the momentum going.",
          "Upper-left ('Steward these'): High engagement + old contact. These donors have a strong history, but nobody has been in touch recently. The engagement score hasn't fully decayed yet, but the clock is ticking. Reach out before the relationship drifts.",
          "Lower-right ('Seeds sprouting'): Low engagement + recent contact. Fresh signals from new or early-stage donors — a first event attendance, a first gift, a recent conversion from prospect. These are emerging relationships worth nurturing.",
          "Lower-left ('Quiet ground'): Low engagement + no recent activity. Donors whose engagement has mostly decayed and who haven't been contacted. They may re-emerge, but right now the energy is elsewhere.",
        ],
      },
      {
        heading: "Positive Deviancy (Glowing Flowers)",
        items: [
          "Some flowers have a pulsing dashed circle around them. These are donors showing positive deviancy — unexpected engagement relative to their position.",
          "A glowing flower means something surprising is happening: a gift after months of silence, engagement across multiple channels, a New Donor scoring well above their peers, or a rapid increase in contact frequency.",
          "The glow is your bee signal. In a field of hundreds, these are the flowers worth visiting first — the donor is already moving toward you.",
        ],
      },
      {
        heading: "Interacting with the Field",
        items: [
          "Hover over any flower to see the Waggle Dance panel on the right — it shows the donor's name, pathway, bloom status, score, and days since last contact.",
          "Click a flower to lock the selection. The panel stays open so you can read the details without holding your mouse steady.",
          "The 'Why this flower is blooming' section lists the specific signals driving that donor's engagement — recent gifts, active contacts, multi-channel engagement, or reactivation after silence.",
          "The suggested next action tells you what to do with what you see — a personal call, a thank-you, a cultivation meeting, or simply watching for the next signal.",
          "Click 'Open Profile' to go directly to the donor's full detail record.",
        ],
      },
      {
        heading: "Filters",
        items: [
          "Blooming (default): Shows only donors with an engagement score above zero — the active field. Dormant donors are hidden so you can focus on where the life is.",
          "All: Shows every donor including dormant ones. Useful for seeing the full landscape and identifying how much of your donor base is currently inactive.",
          "Pathway dots: Click a colored dot to isolate one pathway stage. Click again to deselect. This lets you focus on a specific tier — for example, only green (Engagement) donors to see who's approaching Advocate status.",
        ],
      },
      {
        heading: "What Creates Flowers",
        items: [
          "Engagement score (vertical position) comes from inbound contact log entries: donations (20 pts), volunteering (15 pts), meetings (10 pts), emails/calls/texts (2.5 pts), event attendance (1.5 pts). All points decay to zero over 24 months.",
          "Recency (horizontal position) comes from the most recent contact log entry of any type — including outbound contacts you've logged. Any logged interaction moves the flower right.",
          "Outbound contacts (you reaching out) move the flower right but do NOT raise the score. The score measures their engagement, not your effort. This is the NECTAR principle: authorization is measured by inbound investment.",
          "Bloom level (number of petals) maps to score thresholds: 1 petal at score 1+, 2 at 10+, 3 at 30+, 4 at 50+, and full 5-petal bloom at 80+.",
        ],
      },
      {
        heading: "Reading the Field Over Time",
        items: [
          "Because scores decay, the field is always moving. A flower that was in full bloom three months ago will gradually shrink if no new inbound contacts are logged. This is by design — it forces honesty about relationship health.",
          "After a major event (gala, fundraiser, annual campaign), you should see flowers shift right and upward as attendance and gifts are recorded. If the field doesn't change, ask whether contacts were logged.",
          "A healthy organization shows flowers spread across the upper-right quadrant with a pipeline of sprouts in the lower-right. A field clustered in the lower-left means engagement has gone cold across the board.",
        ],
      },
      {
        heading: "Engagement Alerts",
        items: [
          "The Engagement Alerts widget is the inverse of Pollen View — it surfaces donors who are drifting, gone cold, or at risk. While Pollen View asks 'who's blooming?', Engagement Alerts asks 'who's fading?'",
          "Three alert types: High Value Low Engagement (giving but disengaging), Drifting (60+ days without inbound contact), Gone Cold (score dropped to zero).",
        ],
      },
      {
        heading: "My Day Sidebar",
        items: [
          "Sticky left-side sidebar surfacing the work that protects revenue: tasks due/overdue, your portfolio donors 30+ days without contact, and your portfolio's biggest opportunity gaps by capacity.",
          "Each section shows the top three items plus a 'See all ›' link that deep-links into TasksView, CultivationView, or CapacityView pre-filtered to you.",
          "Click the gear icon at the top of the sidebar to reorder sections (drag), hide ones you don't use, or restore defaults. Preferences save per user.",
          "Click the chevron at the top to collapse the sidebar to a 44px strip — sections shrink to icon + count badge so you can still see the load. Click any badge to re-expand.",
        ],
      },
      {
        heading: "Pipeline Pulse",
        items: [
          "A compact view of your cultivation pipeline showing how many donors are in each pathway stage, average engagement scores, and staleness indicators.",
          "Green = healthy (less than 20% stale), yellow = attention needed (20-50% stale), red = urgent (more than 50% stale).",
        ],
      },
      {
        heading: "Recent Gifts",
        items: [
          "A chronological list of the most recent gifts recorded in the system. Quick reference to see giving activity without navigating to the Gifts view.",
        ],
      },
    ],
    tip: "Start each day on the Dashboard. Scan the Pollen View for glowing flowers — those are your highest-value touchpoints for today. The bee goes where the blooming is.",
  },

  // ──────────────────────────────────────
  // MAIN DONORS VIEW
  // ──────────────────────────────────────
  main: {
    title: "Donors",
    intro:
      "Your donor hub. Two tabs — Donors for browsing and searching your roster, and Manage for adding, importing, and bulk-updating records.",
    workflows: [
      {
        id: "add-new-prospect",
        title: "Add a new prospect to the system",
        goal: "Get a newly-discovered prospect into the CRM without interrupting the conversation you just had.",
        timeEstimate: "3 min",
        steps: [
          {
            text: "Open the Donors view.",
            cta: { kind: "nav", view: "donors", label: "Open Donors" },
            kbd: ["Ctrl", "K", "/d"],
          },
          {
            text: "Switch to the Manage tab and click 'Add Donor.'",
          },
          {
            text: "Enter the essentials: first and last name, email, phone. Set pathway to Prospect — it's the status for donors without a gift.",
          },
          {
            text: "Save. The new record lands on the Donors list with a Prospect badge.",
          },
          {
            text: "Open the profile, go to the Capacity tab, click 'Research this donor' — 15 minutes of public-record work now will pay back for years.",
          },
        ],
        done: "The prospect exists in the system with a pathway, basic contact info, and ideally an initial capacity assessment.",
      },
      {
        id: "bulk-update-list",
        title: "Bulk-update a filtered group of donors",
        goal: "Apply the same change to many donors without clicking into each one.",
        timeEstimate: "5 min",
        steps: [
          {
            text: "Open the Donors view and use the toolbar filters to narrow to the group (pathway + director + flag).",
            cta: { kind: "nav", view: "donors", label: "Open Donors" },
          },
          {
            text: "Switch to the Manage tab and click 'Bulk Update.' The modal loads the currently-filtered set.",
          },
          {
            text: "Pick the field to change (pathway, assigned director, persona, etc.) and the new value.",
          },
          {
            text: "Review the preview count. Apply only when the number matches your mental expectation — over/under by a lot means your filter is off.",
          },
          {
            text: "Save. Records update in-place. An activity log entry is created per donor.",
          },
        ],
        done: "The filter count and the 'updated' confirmation number match, and a spot-check shows the change took effect on at least one donor.",
      },
      {
        id: "import-csv",
        title: "Import donors from a CSV",
        goal: "Bring in a list from an AFP export, an event registrant file, or a prior CRM.",
        timeEstimate: "15 min per 500 rows",
        steps: [
          {
            text: "Switch to the Manage tab and click 'Import CSV.'",
            cta: { kind: "nav", view: "donors", label: "Open Donors" },
          },
          {
            text: "Upload your file. The importer matches known AFP column names automatically; unknown columns are flagged.",
          },
          {
            text: "Review the preview — new vs matched vs skipped counts. The dedupe key is email first, then a name+zip fallback.",
          },
          {
            text: "Commit when the counts look right. Existing donors are updated in place (blanks in the CSV do NOT overwrite existing fields — that's a deliberate safety).",
          },
          {
            text: "After import, switch to the Donors list and filter to 'recently added' to verify.",
          },
        ],
        done: "New donors appear in the roster with the right pathway and contact info; no existing donor lost data.",
      },
    ],
    reference: [
      {
        heading: "Two Tabs",
        items: [
          "Donors tab: Your full donor list with search, filters, and three view modes (Cards, List, Deck).",
          "Manage tab: Add a single donor, import from CSV, or run a bulk update. These tools were moved here to keep the toolbar focused on navigation.",
        ],
      },
      {
        heading: "Giving Quick Glance",
        items: [
          "The collapsible giving strip between the toolbar and tabs shows fiscal year totals: Total Raised, Donors, and Gifts.",
          "Click the strip to expand or collapse. It uses your organization's configured fiscal year (Settings → Organization).",
          "The Momentum Ticker below shows recent activity — new donors, contacts logged, and events.",
        ],
      },
      {
        heading: "Finding Donors",
        items: [
          "Use the search bar in the toolbar to find donors by name, organization, or email.",
          "Click Filters to expand the filter bar — filter by pathway, donor type, donor/board flags, or assigned director.",
          "The alphabet strip on the left side of the card grid lets you jump directly to a last-name letter.",
        ],
      },
      {
        heading: "View Modes",
        items: [
          "Card view groups donors alphabetically with full engagement data visible on each card. The Cards/List/Deck switcher appears in the toolbar only on the Donors tab.",
          "List view shows donors in a compact table — useful for scanning a large roster quickly.",
          "Deck view lets you swipe or arrow through donors one at a time for focused review.",
        ],
      },
      {
        heading: "New Donors Sidebar",
        items: [
          "The 'New' button in the header opens a sidebar showing donors whose start date falls within the last 90 days.",
          "Switch between 30, 60, and 90-day windows using the tabs at the top of the sidebar.",
          "First-time donors (NEW badge) are sorted above renewals within each window, then alphabetically by last name.",
          "Click any name in the sidebar to open that donor's full detail record.",
        ],
      },
      {
        heading: "Manage Tab — Add, Import, Bulk Update",
        items: [
          "Add Donor: Create a new individual, household, or organization donor record.",
          "Import CSV: Load donors and gift history from a CSV file (supports Spektrix and custom formats).",
          "Bulk Update: Find donors by gift data (including by fund), pathway, flags, or director — then update them all at once. Useful for assigning a director to everyone who gave to a specific fund.",
        ],
      },
    ],
    tip: "Use the Manage tab's Bulk Update to assign directors to donors by fund — select the fund, preview the matches, then assign a director in one pass.",
  },

  // ──────────────────────────────────────
  // DONOR DETAIL VIEW
  // ──────────────────────────────────────
  donorDetail: {
    title: "Donor Detail",
    intro:
      "The complete record for a single donor. The 2-Column layout has a sidebar on the left and a tabbed main panel on the right with up to four tabs: Cultivation, Giving, Contact Reports, and Profile.",
    reference: [
      QUICK_COMMAND_BAR_SECTION,
      {
        heading: "Layout & Tabs",
        items: [
          "2-Column mode shows a sticky sidebar on the left (identity, engagement score, dates, classification) and content tabs on the right.",
          "Cultivation tab: Notes, cultivation intelligence, communication history, and suggested next actions.",
          "Giving tab: Gift history, campaigns, and show sponsorships. Hidden for users without gift permissions.",
          "Contact Reports tab: Structured donor visit reports with optional intelligence sections. Admin and Director only.",
          "Profile tab: Personal info, professional details, donor classification, address, and custom fields.",
          "Classic mode stacks all sections vertically — better on smaller screens. The layout toggle is in the toolbar.",
        ],
      },
      {
        heading: "Contact Reports — Capturing Donor Visits",
        items: [
          "Use the Contact Reports tab to file a structured call report after every meaningful donor visit. This is the institutional memory layer that survives staff turnover.",
          "Click + New Contact Report. Six required fields: visit date, meeting type (Discovery / Cultivation / Solicitation / Stewardship), who was present, narrative summary, next step, and next-step due date.",
          "Below the required fields are nine collapsible optional intelligence sections: Family, Occupation, Recreation & Interests, Messaging Emphasis & Constraints, Wealth Signals, Philanthropic Interests, Network & Introductions, Capacity Update, and Strategic Note. Expand only the ones that apply.",
          "Pin to donor record: five sections (Family, Occupation, Recreation, Messaging Emphasize, Messaging Avoid) have a 📌 checkbox. Pinned content gets promoted to the donor's permanent Donor Notes panel on the sidebar — durable intelligence like 'spouse's name is Marian' or 'do not mention the 2019 building campaign.'",
          "Saving a contact report does three things automatically: creates a linked communication log entry (so engagement scoring counts the visit), creates a Director Task with the next-step text and due date, and writes any pinned sections to the donor's permanent record.",
          "The history list shows all past reports for this donor. Click any row to open the formatted print view. Admin users see a small trash icon to delete a report — the linked log entry and follow-up task are preserved.",
        ],
      },
      {
        heading: "Donor Notes (Pinned Intelligence)",
        items: [
          "The Donor Notes panel appears on the sidebar between the gift summary and the action buttons whenever a donor has pinned content from a contact report.",
          "Each pinned card shows the section label, the pinned text, and a footer with the date and officer who pinned it. Click 'View source report' to open the originating contact report.",
          "Re-pinning a section in a new contact report overwrites the existing pin (latest wins). The original report still preserves the historical version.",
          "Click the × on a card to unpin. The original contact report is not affected.",
        ],
      },
      {
        heading: "Portfolio Moves",
        items: [
          "Donors assigned to a director's portfolio follow a 6-stage cultivation cycle: Cultivate → Propose → Acknowledge → Thank → Report → Renew.",
          "The current move and days-in-stage are visible on the portfolio card on the Dashboard.",
          "The assigned director can advance a donor to the next stage or jump to any stage. Every move auto-creates a contact log entry.",
          "Recording a gift automatically advances the donor to the Acknowledge stage.",
          "Completing Renew resets the cycle back to Cultivate for the next round.",
        ],
      },
      {
        heading: "Portfolio Pathway Floor",
        items: [
          "Donors in a portfolio (assigned to a director) are automatically elevated to at least the Engagement pathway — they are being actively managed.",
          "Portfolio donors with lifetime giving over $10,000 are elevated to the Advocate pathway.",
          "If a donor is removed from a portfolio, their pathway reverts to whatever the scoring engine calculates.",
        ],
      },
      {
        heading: "Cultivation Section",
        items: [
          "Three key numbers: Engagement Score, Days Since Last Contact, and Active Contacts (within the 24-month decay window).",
          "The score bar shows where this donor sits, with threshold markers at 10, 30, and 80 — the pathway boundaries.",
          "Click the list icon next to the engagement score in the sidebar to open the Score Audit — a full ledger of every contact and its point contribution.",
          "Suggested Next Move is generated automatically based on the donor's score, donor/board flags, and days since contact.",
        ],
      },
      {
        heading: "Classification & Pathway",
        items: [
          "Pathway and Assigned Director save immediately when changed — no Edit/Save required.",
          "The system auto-assigns pathways by score. You can override manually; an amber indicator appears when a manual override differs from the calculated pathway.",
          "Persona selection is also immediate-save. Click the ⓘ icon next to Persona to see all available personas with descriptions.",
          "Donor and Board flags are checkboxes — check/uncheck saves instantly.",
        ],
      },
      {
        heading: "Record Navigation",
        items: [
          "Use the ‹ › arrows in the toolbar to move between donors without returning to the list.",
          "The position counter (e.g. '12 of 62') reflects your current filter — navigating within a filtered set stays within that set.",
        ],
      },
      {
        heading: "Editing",
        items: [
          "In 2-Column mode, each section on the Profile tab has its own Edit/Save button.",
          "In Classic mode, the global Edit button in the toolbar puts the entire form into edit mode.",
          "Only Admins and Directors assigned to this donor can edit. Board members see a read-only view.",
          "Only Admins can delete a donor record.",
        ],
      },
    ],
    tip: "Use the Cultivation tab for daily relationship management, the Giving tab before stewardship calls, and the Profile tab when updating donor records.",
  },

  // ──────────────────────────────────────
  // CULTIVATION VIEW
  // ──────────────────────────────────────
  cultivation: {
    title: "Cultivation",
    intro:
      "Plan and track outreach to donors. Five tabs give different cuts of the same data — overview, visual pipeline board, prioritized action list, full table, and comms list-builder. Everything is driven by the engagement scoring engine that turns logged contacts into a living score per donor.",
    workflows: [
      {
        id: "tuesday-call-list",
        title: "Work your Tuesday call list",
        goal: "Move five donors forward this week without scrolling or second-guessing.",
        timeEstimate: "30 min, Tuesday morning",
        steps: [
          {
            text: "Open Cultivation and switch to the Actions tab.",
            cta: { kind: "nav", view: "cultivation", label: "Open Cultivation" },
            kbd: ["Ctrl", "K", "/c"],
          },
          {
            text: "In the Director dropdown, pick your own name. Your prioritized list appears.",
          },
          {
            text: "Focus on red urgency dots first — these are donors 30+ days without contact.",
          },
          {
            text: "Click a donor, log the contact right from the profile using Ctrl+K /log.",
            kbd: ["Ctrl", "K", "/log"],
          },
          {
            text: "Repeat until you've moved five donors. Don't try to clear the list in one day.",
          },
        ],
        done: "Five fresh contact logs are saved and the red dots next to those donors have cleared.",
      },
      {
        id: "prep-board-meeting",
        title: "Prepare for a board meeting",
        goal: "Walk in with a defensible pipeline snapshot and a focused talking list.",
        timeEstimate: "45 min, day before",
        steps: [
          {
            text: "Open Cultivation → Pipeline tab. Set the Director dropdown to 'All directors' or keep your name, depending on what you're reporting on.",
            cta: { kind: "nav", view: "cultivation", label: "Open Cultivation" },
          },
          {
            text: "Screenshot the full Kanban board — board members respond to visuals.",
          },
          {
            text: "Switch to the Actions tab. Screenshot the top 10 overdue-urgency donors as your follow-up plan.",
          },
          {
            text: "Open the Capacity view, sort by Opportunity Gap, and screenshot the Summary rail for the dollar headline.",
            cta: { kind: "nav", view: "capacity", label: "Open Capacity" },
          },
          {
            text: "Assemble three slides: visual pipeline, top 10 actions, capacity summary. Add a one-paragraph narrative tying them together.",
          },
        ],
        done: "You have three screenshots and a one-paragraph narrative ready to drop into a board deck.",
      },
      {
        id: "file-contact-report",
        title: "File a substantive contact report after a visit",
        goal: "Capture meeting intelligence in a structure future-you (or your successor) can use.",
        timeEstimate: "10 min after the visit",
        steps: [
          {
            text: "Open the donor's profile and click the Contact Reports tab.",
          },
          {
            text: "Start a new report. Fill the six required fields: visit date, meeting type (discovery / cultivation / solicitation / stewardship), attendees, summary, next step, next step date.",
          },
          {
            text: "Expand optional intelligence sections as needed — family, occupation, recreation, messaging preferences, capacity update.",
          },
          {
            text: "Pin any section worth surfacing on the donor sidebar (e.g. 'do not mention the gala'). This carries the content into the sticky Donor Notes panel.",
          },
          {
            text: "Save. A contact log entry is auto-created with the right date and direction, preserving engagement scoring credit.",
          },
        ],
        done: "The report is saved, the sidebar shows any pinned intelligence, and a follow-up task is scheduled for next_step_date.",
      },
      {
        id: "build-targeted-list",
        title: "Build a targeted list for an appeal or event invite",
        goal: "Send the right message to the right donors without exporting to Excel and bouncing it around in email.",
        timeEstimate: "15 min",
        steps: [
          {
            text: "Open Cultivation → Comms tab.",
            cta: { kind: "nav", view: "cultivation", label: "Open Cultivation" },
          },
          {
            text: "Add field filters (pathway stage, persona, assigned director) using AND logic to narrow the list.",
          },
          {
            text: "Add a giving filter — pick the fund, a date range (use the Fiscal Year quick button), and optionally a minimum amount.",
          },
          {
            text: "Choose a column preset: Email Merge, USPS Mail, or Custom. Contact preference opt-outs are enforced automatically.",
          },
          {
            text: "Copy emails, open in your mail client via mailto:bcc, or export CSV. When the send is done, click 'Mark as Sent' to bulk-log a contact for every donor on the list.",
          },
        ],
        done: "Your list is sent and every donor has an outbound contact log entry on their record.",
      },
      {
        id: "audit-director-portfolio",
        title: "Audit another director's portfolio",
        goal: "Answer 'is this portfolio being worked?' without a standing meeting.",
        timeEstimate: "15 min",
        steps: [
          {
            text: "Open Cultivation → Actions tab and pick that director's name from the Director dropdown.",
            cta: { kind: "nav", view: "cultivation", label: "Open Cultivation" },
          },
          {
            text: "Look at the red overdue dot count. High overdue count = portfolio drift.",
          },
          {
            text: "Switch to Pipeline tab. Columns should have donors in every stage — an empty Advocate column with many New Donors suggests stalled cultivation.",
          },
          {
            text: "Open the Capacity view, filter Portfolio to that director. Avg Confidence tells you whether the portfolio rests on real research or guesses.",
            cta: { kind: "nav", view: "capacity", label: "Open Capacity" },
          },
          {
            text: "Schedule a 1-on-1 with the director focused on the two or three donors that most need attention — not a generic \"how's it going?\"",
          },
        ],
        done: "You have three specific donor names to discuss and a clear read on overall portfolio health.",
      },
    ],
    reference: [
      {
        heading: "The Toolbar — Search, Filter, and Status",
        items: [
          "The search bar on the left filters by donor name, organization, or assigned director. It applies to all five tabs at once — type a name and every tab updates.",
          "The Director dropdown on the right filters to a single director's portfolio. Select your own name before a board meeting to see only the donors you are responsible for.",
          "The red 'overdue' badge shows how many donors across the organization haven't been contacted in over 30 days. This is your at-a-glance urgency indicator.",
          "The count next to the dropdown (e.g. '48 of 62') shows how many donors match your current search and filter.",
          "Your active tab is remembered between visits — if you leave Cultivation and come back, you'll land on the same tab.",
        ],
      },
      {
        heading: "Overview Tab — Organization Health",
        items: [
          "A dashboard showing how your organization's engagement breaks down across stages, with momentum trends and the top-scoring donors.",
          "Use this tab when you need a quick answer to 'how is the organization doing overall?' before a board meeting or planning session.",
        ],
      },
      {
        heading: "Pipeline Tab — The Visual Board",
        items: [
          "A horizontal Kanban-style board with columns for each engagement stage: Prospect → New Donor → Donor of Record → Engagement → Advocate, flowing left to right.",
          "Each donor appears as a card showing their name, engagement score, days since last contact (color-coded green/amber/red), badges (Board, Donor, Prospect), and a plain-language Next Best Action.",
          "Within each column, donors are sorted by urgency — overdue donors float to the top so you see who needs attention first.",
          "Click any card to open a slide-out drawer with the donor's score details, recent activity, and a button to open their full profile.",
          "The Pipeline tab fills the full screen width — it doesn't scroll vertically like the other tabs. Each column scrolls independently.",
          "If a director has manually overridden a donor's pathway, that donor appears in the overridden column, not the score-calculated one.",
        ],
      },
      {
        heading: "Actions Tab — Your To-Do List",
        items: [
          "Every donor in the system gets a Next Best Action generated by the engine — this tab lists them all, sorted by urgency.",
          "Red urgency dots mean overdue (30+ days without contact). Amber means approaching. Green means on track.",
          "Use this as your weekly outreach plan: filter to your own name in the Director dropdown, then work down the list.",
          "Click any donor to open their full detail record where you can log a contact or update their planned next step.",
        ],
      },
      {
        heading: "Donors Tab — The Full Table",
        items: [
          "A searchable, sortable table of all donors with their engagement score, pathway, days since contact, and assigned director.",
          "Use this when you need to scan the full roster quickly or find a specific donor's engagement data.",
        ],
      },
      {
        heading: "Comms Tab — List Builder",
        items: [
          "Build targeted donor lists using stackable field filters (AND logic) and a giving filter with fund, date range, and minimum amount controls.",
          "Quick date range buttons (Today, Last 30 Days, This Month, Fiscal Year, etc.) pre-fill the giving filter dates with one click.",
          "Filter by fund to find all donors who gave to a specific fund within a date range.",
          "Column presets (Email Merge, USPS Mail, Custom) configure the output format. Contact preference opt-outs are enforced automatically.",
          "Actions: Copy Emails (Outlook-compatible semicolon list), Open in Email (mailto:bcc=), Export CSV, and Mark as Sent (bulk contact log with direction tracking).",
          "Save and reload list configurations for recurring outreach campaigns.",
        ],
      },
      {
        heading: "How Engagement Scoring Works",
        items: [
          "Every logged contact earns points based on its type: quick contacts (email, phone, text) earn 2.5 pts, meetings (coffee, lunch) earn 10 pts, volunteering earns 15 pts, donations earn 20 pts, and event attendance earns 1.5 pts.",
          "Points decay linearly to zero over 24 months. A coffee meeting today is worth 10 pts; the same meeting one year later is worth 5 pts; at two years it reaches zero. This means the score reflects recent engagement, not lifetime history.",
          "Only inbound contacts earn points — outbound contacts (you reaching out to them) are recorded for history but score zero. The score measures their engagement, not your effort.",
          "Pathway thresholds: New Donor is 0–9 pts, Donor of Record is 10–29 pts, Engagement is 30–79 pts, Advocate is 80+ pts. Donors move between stages automatically as their score changes.",
          "Prospect is a special status outside the scoring engine. It is manually assigned (typically when a non-donor is added at an event) and stays until a director changes it.",
          "Point values, thresholds, and the decay period can all be adjusted by an Admin in Settings → Scoring.",
        ],
      },
    ],
    tip: "Before your board meeting: select your name in the Director dropdown, check the Actions tab for your prioritized to-do list, then switch to Pipeline to see your donors visually by stage. Export or screenshot either view for your meeting notes.",
  },

  // ──────────────────────────────────────
  // CAPACITY VIEW
  // ──────────────────────────────────────
  capacity: {
    title: "Capacity",
    intro:
      "A decision-support tool. At the portfolio level it answers \"where should I spend my time this week?\" At the donor level, \"what's my realistic ask and can I defend it?\" Not a form to fill out — a ranked triage list paired with a confidence score that tells you how defensible each estimate is.",
    workflows: [
      {
        id: "weekly-triage",
        title: "Triage your portfolio this week",
        goal: "Walk into Monday knowing exactly which 10 donors to focus on.",
        timeEstimate: "20 min, Monday morning",
        steps: [
          {
            text: "Open the Capacity view.",
            cta: { kind: "nav", view: "capacity", label: "Open Capacity" },
            kbd: ["Ctrl", "K", "/capacity"],
          },
          {
            text: "Set the Portfolio filter to 'Mine' so you only see donors assigned to you.",
          },
          {
            text: "Leave the sort at Opportunity Gap (the default). Highest unrealized capacity floats to the top.",
          },
          {
            text: "Scan the top 10 rows. Screenshot or note them — this is your week's focus list.",
          },
          {
            text: "Click any row to open that donor's Capacity tab and read the Next Move prompt.",
          },
        ],
        done: "You have 10 named donors and a next action for each.",
      },
      {
        id: "research-new-prospect",
        title: "Research a new prospect in 15 minutes",
        goal: "Turn a SUGGESTED band into a confirmed one using free public records.",
        timeEstimate: "15 min per donor",
        steps: [
          {
            text: "Open the donor profile and switch to the Capacity tab.",
          },
          {
            text: "Click 'Research this donor' in the Wealth Evidence card. A modal opens with a form and a cheat-sheet of free public sources linked at the bottom.",
          },
          {
            text: "Open each source in a new tab: county assessor for real estate, SEC EDGAR for insider stock, FEC.gov for political giving, ProPublica Nonprofit Explorer for 990-PF foundation affiliations, LinkedIn for business roles.",
          },
          {
            text: "Type what you find into the structured fields — dollars accept '$1.2M' and '$1,234,567' without cleanup.",
          },
          {
            text: "Save. The green MANUAL RESEARCH badge appears on the donor's Wealth Profile card and confidence climbs by up to 15%.",
          },
        ],
        done: "The donor's confidence score has jumped and the SUGGESTED italics are gone.",
      },
      {
        id: "log-capacity-assessment",
        title: "Log your capacity assessment on a donor",
        goal: "Capture the judgment that only you can bring — and get the 20% Director Assessment weight credited.",
        timeEstimate: "3 min per donor",
        steps: [
          {
            text: "Open the donor's Capacity tab. If the Director's Assessment card is empty, click 'Enter Capacity Assessment.'",
          },
          {
            text: "Pick a 5-year capacity band. Be honest — over-rating is as harmful as under-rating.",
          },
          {
            text: "Choose an inclination (cold / warm / hot / ready) and your confidence level in the estimate (low / medium / high).",
          },
          {
            text: "Tag where this estimate came from (direct conversation, public record, wealth screen, intuition).",
          },
          {
            text: "In the narrative field, write what you know that isn't in any database — \"met at the gala, mentioned estate planning twice, spouse chairs the county hospital board.\" This survives staff turnover.",
          },
        ],
        done: "The headline confidence dial reflects your judgment and the narrative text is visible on the card.",
      },
      {
        id: "band-bump-review",
        title: "Find donors ready for a capacity band bump",
        goal: "Surface donors who've outgrown their rating so you ask for more next time.",
        timeEstimate: "10 min at quarter's end",
        steps: [
          {
            text: "Open the Capacity view.",
            cta: { kind: "nav", view: "capacity", label: "Open Capacity" },
          },
          {
            text: "In the Summary rail on the right, find 'At or above rating' and click the tile.",
          },
          {
            text: "The list filters to donors whose 5-year giving has met or exceeded their current band's midpoint. The FILTERED badge lights up.",
          },
          {
            text: "Open each donor, review their recent giving, and raise the capacity band a tier. Save.",
          },
          {
            text: "Click the tile again to clear the filter and return to the full triage list.",
          },
        ],
        done: "Every donor on the isolate list has either a new higher band or a note explaining why you left it alone.",
      },
      {
        id: "board-ready-summary",
        title: "Prepare a board-ready capacity summary",
        goal: "Give the ED or board a defensible snapshot of portfolio potential.",
        timeEstimate: "15 min before a board meeting",
        steps: [
          {
            text: "Open the Capacity view with the Portfolio filter set to 'All donors' (or a specific director you're reporting on).",
            cta: { kind: "nav", view: "capacity", label: "Open Capacity" },
          },
          {
            text: "Screenshot the Summary rail: total rated capacity, 5-year giving, opportunity gap, average confidence, at-or-above-rating count.",
          },
          {
            text: "Filter by Confidence → High (75%+) and screenshot the resulting list — this is the defensible portfolio.",
          },
          {
            text: "Filter by Confidence → Low (below 40%) — this is the research queue. A short board narrative writes itself from these two cuts.",
          },
          {
            text: "Open the Pollen view's Second Gift Opportunities card for a complementary pipeline picture.",
          },
        ],
        done: "You have 3 screenshots and a one-paragraph narrative ready to drop into your board deck.",
      },
      {
        id: "recover-low-confidence",
        title: "Shore up a donor with big capacity but low confidence",
        goal: "Raise a shaky estimate before you ask — avoid over-asking on thin data.",
        timeEstimate: "25 min per donor",
        steps: [
          {
            text: "On the donor's Capacity tab, read the Confidence Breakdown. The component with the lowest score and an amber hint is your target.",
          },
          {
            text: "If 'Relationship depth' is low, log a substantive contact or schedule a coffee meeting this week.",
            kbd: ["Ctrl", "K", "/log"],
          },
          {
            text: "If 'Director assessment' is low, step through the Log-a-capacity-assessment workflow above.",
          },
          {
            text: "If 'External wealth data' is low, step through the Research-a-new-prospect workflow above.",
          },
          {
            text: "Refresh the Capacity tab. The dial should climb. When it's above 60%, you're ready to plan the ask.",
          },
        ],
        done: "Overall confidence is 60%+ and every component has a non-amber score.",
      },
    ],
    reference: [
      {
        heading: "The Workflow — Triage · Assess · Act · Justify",
        items: [
          "TRIAGE — Land on this view and scan the top of the list. Default sort is 'Opportunity Gap' descending, which surfaces donors whose rated capacity is highest relative to what they've actually given you in the last five years. These are your highest-leverage prospects.",
          "ASSESS — Click any row. You land directly on that donor's Capacity tab (not Profile) because you're in assessment mode. You'll see their full picture: observed signals the system pulled automatically (giving, engagement, contact cadence), any imported wealth-screen data, and the confidence breakdown explaining why the score is what it is.",
          "ACT — From the Capacity tab, log a contact, enter your assessment, or move the donor in the pipeline — no context switching. Use Ctrl+K /log to open the Log Contact modal without scrolling.",
          "JUSTIFY — When the ED or board asks 'why are you focused on X?', the Capacity tab IS the justification document. The confidence score is the data trail, the narrative note is your reasoning, the imported wealth data is your evidence.",
        ],
      },
      {
        heading: "Reading the Portfolio List",
        items: [
          "DONOR column — Name, pathway stage, assigned director. Click the row to open them at the Capacity tab.",
          "CAPACITY column — A dollar band representing what this donor could give over 5 years if maximally inclined. Shows 'SUGGESTED' in italics when it's system-inferred from giving history; shows the band in normal weight once a director has confirmed it.",
          "CONFIDENCE column — A 0–100% score showing how defensible the capacity estimate is. Low confidence means 'you shouldn't bet the quarter's plan on this number yet.' High confidence means 'this is well-supported.'",
          "5YR GAP column — Capacity band midpoint minus what the donor has given in the last 5 years. A high gap means high upside. Zero gap means the donor is giving at or above their rated capacity — consider raising the band.",
          "LAST TOUCH column — Days (or months, years) since the most recent gift or logged contact, whichever is more recent.",
          "NEXT MOVE column — The single highest-leverage action to strengthen the capacity call, pulled from the confidence breakdown's gap analysis.",
        ],
      },
      {
        heading: "Sorting — Four Lenses on the Same List",
        items: [
          "OPPORTUNITY GAP (default) — Biggest unrealized capacity on top. Best for 'who should I focus on?' planning.",
          "CAPACITY — Highest rated capacity on top. Best for major-gift prospecting.",
          "CONFIDENCE (lowest first) — Best for 'who do I need to research?' — surfaces donors whose estimates are weakest and would benefit from a meeting, a note, or wealth screening.",
          "LAST CONTACT (oldest first) — Best for 'who have I been neglecting?' — surfaces donors who've gone quiet regardless of their capacity band.",
        ],
      },
      {
        heading: "Filtering Your View",
        items: [
          "PORTFOLIO — Default is 'All donors.' Switch to 'Mine' to see only the donors you're assigned. Pick a specific director's name to audit their portfolio.",
          "CAPACITY BAND — Filter to a single band (e.g. $10K–$50K) to find prospects in that tier, or pick 'Not yet estimated' to find donors where the band is still system-suggested and needs director sign-off.",
          "CONFIDENCE — 'Low' (below 40%) surfaces research-needed donors; 'High' (75%+) surfaces the portfolio you can defend confidently.",
          "PATHWAY STAGE — Filter to a specific cultivation stage to pair the capacity lens with your engagement pipeline.",
          "CLICK-TO-FILTER FROM THE SUMMARY RAIL — The 'At or above rating' metric in the right-hand Summary rail is clickable. Click it to isolate donors who are giving at or past their rated capacity (a signal that their band may need raising). A 'FILTERED' badge appears while the filter is active; click again to clear.",
        ],
      },
      {
        heading: "The Summary Rail — Portfolio-Level Metrics",
        items: [
          "TOTAL RATED CAPACITY — Sum of every donor's capacity band midpoint, with a breakdown: 'X confirmed · Y suggested.' Confirmed means a director has assigned the band; suggested means the system inferred it from giving history.",
          "5-YEAR GIVING — What donors have actually given in the last 5 years, summed across the view.",
          "OPPORTUNITY GAP — Total capacity minus 5-year giving. The single number that represents 'upside we haven't captured yet.' Your plan should have a credible theory for chipping away at this.",
          "AVG CONFIDENCE — Average confidence score across the view. A low average means your portfolio model rests on soft ground — prioritize research or assessment work.",
          "AT OR ABOVE RATING — Donors giving at or past their rated capacity. Click this tile to isolate them — they're candidates for a band-bump review.",
        ],
      },
      {
        heading: "What You See on the Donor's Capacity Tab",
        items: [
          "HEADLINE DIAL — The donor's capacity band + confidence % at a glance. Amber alert strip below the headline surfaces the single highest-leverage next move.",
          "DIRECTOR'S ASSESSMENT — Your entered judgment: capacity band, inclination (cold/warm/hot/ready), your confidence in the estimate (low/med/high), the source (direct conversation / public record / wealth screen / intuition), and a narrative note. Blank fields score zero; a filled assessment adds up to 20% to the overall confidence.",
          "WHAT THE SYSTEM SEES — Observed signals drawn automatically from gifts and contact logs: lifetime giving, largest single gift, gift count, years giving, contacts in the last 12 months, engagement score. Use these to inform your assessment above.",
          "WEALTH PROFILE (when imported) — Appears below the observed signals once a DonorSearch or compatible CSV has been matched to this donor. Shows vendor rating, capacity score, real estate value, public stock value, FEC political giving total, and counts of foundation and business affiliations, with imported-on date and source badge.",
          "CONFIDENCE BREAKDOWN — Six weighted components with per-component scores and a gap-analysis hint on any component scoring under 70 ('Log a substantive contact — the fastest way to raise this score.'). This is the feature's differentiator: it tells you what to do next.",
          "NEXT ACTION ROW — One-click Log Contact button + Adjust Estimate / Enter Assessment. No digging through tabs to take action.",
        ],
      },
      {
        heading: "The Confidence Score — Six Weighted Components",
        items: [
          "GIVING HISTORY DEPTH (default 25%) — Gift count, tenure in your donor file, recency of last gift. More and more-recent gifts raise this.",
          "RELATIONSHIP DEPTH (default 20%) — Contact logs in the last 12 months, plus filed contact reports. Substantive meetings raise this faster than one-line log entries.",
          "DIRECTOR ASSESSMENT (default 20%) — Your own judgment counts. When you enter a capacity band + confidence level + narrative notes, this component rises. A blank assessment scores zero.",
          "EXTERNAL WEALTH DATA (default 15%) — DonorSearch / iWave / WealthEngine imports. For organizations with no imports, this component silently rebalances to zero weight so small shops aren't penalized. The moment any CSV import exists for the organization, the weight activates automatically for every donor with a profile.",
          "ENGAGEMENT SIGNAL (default 10%) — The engagement score from the Cultivation engine. Events, logged contacts, and donations feed this.",
          "NETWORK & ROLE (default 10%) — Board service, committee participation, persona tags, and organizational affiliation.",
          "An Admin can tune these six weights in Settings → Scoring → Capacity Confidence Weights. Sliders auto-rebalance to 100% as you drag. Changes apply organization-wide and take effect instantly across every donor.",
        ],
      },
      {
        heading: "Suggested vs Assigned Capacity — Why Some Rows Show 'SUGGESTED'",
        items: [
          "When no director has confirmed a capacity band, the system infers one from 5-year giving × 5 (industry rule-of-thumb: donors give 5–10% of real capacity annually). This lets the triage view work on day one, before you've assessed anyone.",
          "Suggested bands are labeled 'SUGGESTED' in italics. They still drive sort, filter, and summary math — but they are transparently marked as system inferences, never passed off as director judgment.",
          "Open the donor's Capacity tab and enter your assessment to convert a 'SUGGESTED' band into a confirmed one. Your entry always overrides the suggestion.",
          "The Summary rail's 'TOTAL RATED CAPACITY' hint shows the split — e.g. '12 confirmed · 48 suggested' — so you can see at a glance how much of the portfolio is your judgment vs the system's guess.",
        ],
      },
      {
        heading: "Entering Your Own Research (manual wealth evidence)",
        items: [
          "On any donor's Capacity tab, look for the 'Wealth Evidence' card. When no profile exists yet you'll see a 'Research this donor' button; once data is on file the card shows the figures and an 'Edit research' button in the top-right.",
          "The Research modal captures the same fields a paid wealth screen would: real estate value, public stock value, political giving total (trailing 24 months), foundation affiliations (repeatable, name + role), business or board affiliations (repeatable, company + role), and free-form notes.",
          "Dollar fields understand '$1.2M', '$1,234,567', and 'n/a' — no need to clean up the number yourself.",
          "A cheat-sheet inside the modal lists the five free public sources to consult: county assessor, SEC EDGAR, FEC.gov, ProPublica Nonprofit Explorer (990-PFs), and LinkedIn.",
          "Manual research counts for the same 15% External Wealth weight as a DonorSearch import. A shop with zero subscriptions but thorough research on its top 50 prospects reaches the same confidence ceiling as a shop with a paid subscription.",
          "Editing a vendor-imported profile keeps the vendor source badge (you're refining, not replacing the attribution). Pure manual rows display a green 'MANUAL RESEARCH' badge.",
          "If you later run a DonorSearch CSV import and it matches donors with manual research on file, the import preview warns you before overwriting. The original row is always preserved in raw_import for forensic recovery.",
        ],
      },
      {
        heading: "Importing Wealth-Screen Data (DonorSearch / iWave / WealthEngine)",
        items: [
          "Go to Settings → Data → Wealth Screening Import. Admin-only. Upload your vendor's CSV export — column names from DonorSearch are recognized automatically, and iWave / WealthEngine formats with similar columns work too.",
          "PREVIEW STEP — Before anything is saved, you see a match breakdown: how many rows matched by email (strongest), by name + city (strong), by name only (weaker — shown in amber), and how many were ambiguous or unmatched. Ambiguous and unmatched rows are dropped with a count — this tool does not create new donors.",
          "UNMAPPED COLUMNS — If your CSV has columns we don't recognize, they're flagged in a warning but preserved in a raw_import JSON field per row, so you can always look back at the original values even after we overwrite on re-import.",
          "COMMIT — Click the 'Import X profiles' button to save. Matched rows overwrite any existing wealth profile for that donor (latest import wins). The CapacityView and the donor's Capacity tab refresh automatically.",
          "RE-IMPORT — When your DonorSearch subscription gives you a fresh file six months later, just re-import. Overwrite-in-place keeps the model current without requiring you to reconcile deltas manually.",
          "NUMERIC PARSING — The mapper understands '$1.2M', '$1,234,567', and 'n/a' — no need to clean the file before uploading.",
        ],
      },
      {
        heading: "For Small Shops Without a Wealth Screen",
        items: [
          "This view works fully without any wealth-screening subscription. The External Wealth Data component silently rebalances to zero weight when no imports exist, so small shops aren't penalized with a permanent ceiling on confidence.",
          "Your observed signals (giving, contact logs, engagement, events) plus your own director assessment get you to meaningful confidence scores (70–90%) without ever paying for wealth data.",
          "The gap analysis on each confidence breakdown will tell you exactly what to do next — log a contact, enter your assessment, file a contact report — which are all free moves.",
          "If you do subscribe later, Settings → Data → Wealth Screening Import accepts the CSV and the 15% External Wealth component activates automatically across the whole organization.",
        ],
      },
    ],
    tip: "Start of the week, open this view, filter to 'Mine,' sort by 'Opportunity Gap,' and work the top 10. Open each donor — you'll land directly on their Capacity tab — read the confidence breakdown, take the 'Next Move' action the system suggests. At the end of the quarter, you'll have a defensible paper trail for every donor you touched, and the Summary rail will tell the story of your portfolio's growth.",
  },

  // ──────────────────────────────────────
  // DIRECTOR PORTFOLIOS VIEW
  // ──────────────────────────────────────
  portfolios: {
    title: "Director Portfolios",
    intro:
      "This view shows which donors are assigned to which director for relationship management. Every donor should have a director looking out for them. Start with the Overview to see organization-wide balance, then drill into any portfolio to see the individual donors.",
    sections: [
      {
        heading: "What You See First (Overview)",
        items: [
          "When you open Director Portfolios, you land on the Overview — a snapshot of how donor assignments are distributed across the directors.",
          "Four stat tiles across the top tell you: how many directors are active, how many donors have been assigned to someone, how many are unassigned, and the average portfolio size per director.",
          "Below the stats, each director has a card showing their name, initials badge, total assigned count, and a row of colored pathway badges (e.g. 'New Donor: 4, Donor of Record: 7'). These badges show you the engagement mix — a portfolio heavy on New Donor stage needs more outreach.",
          "If any donors have no director assigned, an amber Unassigned card appears. This is your action item — those donors aren't in anyone's cultivation plan.",
        ],
      },
      {
        heading: "Drilling Into a Portfolio",
        items: [
          "Click any director card on the Overview to see that person's full portfolio — or use the dropdown at the top of the screen to select a director by name.",
          "The dropdown also shows a donor count next to each name (e.g. 'Jane Smith (12)') so you can quickly see workload without opening each portfolio.",
          "Inside a portfolio, you see full donor cards with engagement scores, pathway colors, days since last contact, and all the same information visible on the main Donors view.",
          "Click any donor card to open their full detail record — you can log contacts, review their history, and update their classification from there.",
          "A search box appears in the toolbar when viewing a portfolio — use it to find a specific donor by name or organization within that director's list.",
        ],
      },
      {
        heading: "The Unassigned List",
        items: [
          "Select 'Unassigned' from the dropdown (or click the amber card on the Overview) to see every donor without a director.",
          "These donors are not in anyone's cultivation pipeline — no director is responsible for building a relationship with them.",
          "Review this list regularly. After events, new donors and prospects often land here before someone claims them.",
        ],
      },
      {
        heading: "How Assignments Work",
        items: [
          "Donor assignments are made from the donor's own detail record, not from this view. Open the donor, find 'Assigned Director' under Classification, and select a director's name.",
          "Only donors flagged as 'Board Member' in their profile appear in the director dropdown. If a director is missing, check that their Board flag is checked.",
          "Each donor can only be assigned to one director at a time. Changing the assignment moves them between portfolios instantly.",
          "If a director's access is deleted by an Admin in Settings → Users, their assigned donors become unassigned and will appear in the Unassigned list.",
        ],
      },
      {
        heading: "No Board Members?",
        items: [
          "If you see a message saying 'No Board Members Flagged' with a crown icon, it means no donors in the system have been flagged as board members yet.",
          "To fix this, go to the Donors list, open each board member's detail record, and check the 'Board' flag under Classification. They will appear here as directors immediately.",
        ],
      },
    ],
    tip: "Check the Unassigned list after every event — new prospects and donors often arrive without a director. A quick assignment ensures someone follows up while the connection is fresh.",
  },

  // ──────────────────────────────────────
  // REPORTS VIEW
  // ──────────────────────────────────────
  reports: {
    title: "Reports & Analytics",
    intro:
      "Thirteen report tabs that answer different questions about your organization's health: 'How much did we raise?' (Giving), 'Who's slipping?' (Retention), 'What can we expect?' (Forecast), 'Who are the heavy lifters?' (Rankings, Snapshot), 'Are we visiting our top prospects?' (Contact Reports). Use these before board meetings, for annual reporting, or any time you need a defensible number.",
    workflows: [
      {
        id: "board-meeting-prep",
        title: "Build a board fundraising report",
        goal: "Walk in with the year-to-date number, the trend behind it, and a credible projection.",
        timeEstimate: "20 min",
        steps: [
          {
            text: "Open Reports → Giving. Set the date range to fiscal year-to-date and screenshot the Summary tile strip.",
            cta: { kind: "nav", view: "reports", label: "Open Reports" },
          },
          {
            text: "Switch to the Forecast tab. Screenshot the projection KPIs and the segment risk callout.",
          },
          {
            text: "Switch to the Retention tab. Screenshot the split-retention card showing first-time vs repeat performance year-over-year.",
          },
          {
            text: "Three slides: where we are, where we're heading, who's coming back. Tie them together with one paragraph of narrative.",
          },
        ],
        done: "You have three screenshots and a one-paragraph story ready to drop into a board deck.",
      },
      {
        id: "monthly-reconciliation",
        title: "Monthly reconciliation against an external system",
        goal: "Catch missing or duplicate gifts before month-end close.",
        timeEstimate: "15 min",
        steps: [
          {
            text: "Open Reports → Reconcile.",
            cta: { kind: "nav", view: "reports", label: "Open Reports" },
          },
          {
            text: "Upload the Spektrix or Excel CSV for the period. Fund-level and row-level discrepancies populate side-by-side.",
          },
          {
            text: "Sort by absolute discrepancy and work the largest gaps first. Each row links to the underlying donor or gift.",
          },
          {
            text: "Resolve by editing the CRM gift, importing the missing rows from the Spektrix Import modal launched in-place, or flagging a source data error.",
          },
        ],
        done: "Fund totals match between the CRM and the source system, or every remaining discrepancy is documented.",
      },
      {
        id: "monthly-givers-cleanup",
        title: "Flag missed recurring donors",
        goal: "Find donors whose gift cadence makes them recurring even if no one ticked the box.",
        timeEstimate: "5 min",
        steps: [
          {
            text: "Open Reports → Monthly Givers. The detection rule (9+ gifts in the trailing 12 months, median 25–40 day gap) flags candidates automatically.",
            cta: { kind: "nav", view: "reports", label: "Open Reports" },
          },
          {
            text: "Review the 'detected but not flagged' list. Click a row to mark the donor as recurring without leaving the report.",
          },
          {
            text: "Once flagged, stewardship and renewal logic treat the donor correctly going forward.",
          },
        ],
        done: "Every donor matching the cadence rule is either flagged or explicitly excluded.",
      },
    ],
    reference: [
      {
        heading: "Giving — How much, when, what fund",
        items: [
          "Summary section: KPI tiles for total raised, gifts logged, donors giving, and average gift in the chosen date range.",
          "Detail section: gifts grouped by fund, broken down by month — spot which fund drove which month.",
          "Quick-range buttons (FY, calendar year, last 30/90 days) plus custom date range. Range applies to both sections.",
        ],
      },
      {
        heading: "Dashboard — Headline counts",
        items: [
          "KPI tiles: Total Donors, Active Donors, Board Members, Assigned (have a director managing them), and Unassigned. A high Unassigned number is a red flag — those donors have nobody managing them.",
          "Pathway Distribution chart shows where the bulk of your donor base sits on the engagement spectrum (New Donor → Donor of Record → Engagement → Advocate, plus Prospect).",
          "Data Quality strip: percent of donors with email, organization, assigned director, and Org Pays Dues. Low numbers point to data hygiene work.",
        ],
      },
      {
        heading: "Pathways — Who is where",
        items: [
          "Same pathway chart as the Dashboard tab plus a full per-pathway donor table.",
          "Useful for working a single tier — review every New Donor when planning outreach, or check Advocate to identify stewardship opportunities.",
        ],
      },
      {
        heading: "Portfolios — Director workload",
        items: [
          "Workload bar chart at the top — uneven bars mean unbalanced director loads.",
          "Per-director tables showing assigned donors with Name, Pathway, Organization, Email, and Donor flag. Each table exports independently.",
          "Unassigned section appears at the bottom whenever any donors lack a director, with its own export.",
          "Go-to tab for board meeting prep — export each director's table and hand them their cultivation list.",
        ],
      },
      {
        heading: "Donors — Roster + headline donor metrics",
        items: [
          "KPI tiles: Total Donors, Donor Rate (percent of donors who are active givers), and Board + Donor (board members who lead with their own gifts).",
          "Full donor roster with Name, Organization, Email, Pathway, Director, and Board flag. Exports the most complete CSV available.",
        ],
      },
      {
        heading: "Snapshot — Per-donor giving profile",
        items: [
          "One row per donor: gift count, average gift, largest gift, lifetime giving, recency, consecutive fiscal years, current giving level, and pathway.",
          "Sortable columns plus a giving-by-fund pie chart. Use it to see at a glance who's drifted, who's accelerating, and who's a multi-year loyalist.",
          "Date range filter narrows the giving columns; consecutive-year counts use full fiscal-year boundaries from your Organization settings.",
        ],
      },
      {
        heading: "Forecast — What can we expect this year",
        items: [
          "Annual revenue projection built off prior fiscal-year giving as the baseline, with confidence-band callouts.",
          "Segment breakdown shows which donor groups are pacing ahead or behind their prior-year contribution.",
          "Donor table flags top contributors at risk of underperforming. Monthly cash-flow projection at the bottom helps with budget pacing.",
        ],
      },
      {
        heading: "Rankings — Top givers by period",
        items: [
          "Two modes: Top N (default Top 100) or Threshold (default $1,000+). Switch the toggle to change the question.",
          "Date range with quick-range buttons (FY, calendar year, last 12 months). Exports a CSV ready for board packets or major-gift portfolios.",
        ],
      },
      {
        heading: "Retention — Are donors coming back",
        items: [
          "Split retention card: first-time donor retention vs. repeat donor retention, with year-over-year trend arrows.",
          "Cohort heatmap: each acquisition year reads across to retention rate at year+1, year+2, etc. Soft greens are good; deepening reds show donors slipping away.",
          "Tooltip popups explain every metric — click the small question mark next to any number for the definition.",
        ],
      },
      {
        heading: "Monthly Givers — Detect un-flagged recurring donors",
        items: [
          "Detection rule: 9+ gifts in the trailing 12 months AND median gap between gifts of 25–40 days.",
          "Surfaces three groups: confirmed recurring (flagged + matches detection), missed recurring (matches detection but not flagged), and stale flags (flagged but cadence no longer matches).",
          "Click a 'detected but not flagged' row to set the recurring flag inline — no need to open the donor record.",
        ],
      },
      {
        heading: "Reconcile — CRM vs. external gift export",
        items: [
          "Upload a CSV (Spektrix export, Excel sheet, etc.). The report compares it against gifts in the CRM for the same period.",
          "Two views: fund-level (totals per fund — fastest way to spot a missing chunk) and row-level (gift-by-gift discrepancies).",
          "From inside the report you can launch the Spektrix Import modal to bring missing rows in directly.",
        ],
      },
      {
        heading: "Activity — Contact log volume and mix",
        items: [
          "KPI tiles: total contacts, unique donors touched, average contacts per donor in the date range.",
          "Two bar charts: contacts by type (call, email, meeting, etc.) and contacts by purpose (cultivation, stewardship, solicitation, etc.).",
          "Detail table lists every contact with donor name and notes; CSV exportable for activity audits.",
        ],
      },
      {
        heading: "Contact Reports — Cross-donor rollup (Admin / Director only)",
        items: [
          "Two views inside the tab: Recent Reports and Compliance Gap.",
          "Recent Reports: filtered list with date range (default last 90 days), meeting type, and officer filters. The stat strip shows total reports plus a per-meeting-type breakdown to expose cultivation-vs-stewardship balance.",
          "Compliance Gap (90d): every active prospect (Connect / Contribute / Lead pathways) without a contact report in the last 90 days. Days Since is color-coded — amber at 90+, red at 180+ or never. Use this before every staff meeting to triage stewardship gaps.",
          "Click any row to open the formatted report; the small Open → button drills into the donor record instead.",
          "Hidden from Data Analyst role. Admin and Director only.",
        ],
      },
      {
        heading: "Exporting Data",
        items: [
          "Look for the Export CSV button on any data table. Most useful exports: per-director Portfolios tables, Unassigned, Donor Roster, Rankings, Snapshot, and Activity detail.",
          "Filenames follow the pattern <ReportName>_<YYYY-MM-DD>.csv (e.g. Donor_Roster_2026-04-29.csv).",
          "CSV files include all visible columns and open in Excel, Google Sheets, or any spreadsheet app.",
        ],
      },
    ],
    tip: "Different tabs answer different questions. Before opening this view, decide which question you're trying to answer — Giving tells you what happened, Forecast tells you what's coming, and Retention tells you whether the relationship engine is working.",
  },

  // ──────────────────────────────────────
  // EVENTS VIEW
  // ──────────────────────────────────────
  events: {
    title: "Events",
    intro:
      "Track events and attendance in one place. Checking a donor present automatically logs an event contact and updates their engagement score.",
    sections: [
      {
        heading: "Creating Events",
        items: [
          "Click New Event and fill in the name, date, type, and an optional description.",
          "Eight event types are available: Luncheon, Workshop, Mixer, Conference, Fundraiser, Board Meeting, Webinar, and Other.",
          "Events appear in the list grouped by month, newest first.",
        ],
      },
      {
        heading: "Taking Attendance",
        items: [
          "Click Take Attendance on any event card to open the attendance checklist.",
          "Donors are listed alphabetically by last name. Check each person who attended.",
          "Three live stat cards update as you check donors: Donors (active donors), Guests (prospects), and Total.",
          "Use the search box to find a specific donor quickly in a large roster.",
          "Select All and Clear All buttons let you start from a full or empty list.",
          "The 'Who Didn't Attend' toggle filters the list to show only unchecked donors — useful for following up with absentees.",
        ],
      },
      {
        heading: "Non-Donor Guests (Prospects)",
        items: [
          "Use the orange Add New Person button at the top of the attendance checklist to register a guest who is not yet a donor.",
          "You must enter first name, last name, email, and organization — all four fields are required.",
          "The guest is added to the system with a Prospect pathway and immediately checked as attending this event.",
          "Prospects appear in the donor list and can be cultivated toward active engagement over time.",
        ],
      },
      {
        heading: "How Attendance Affects Scores",
        items: [
          "Saving attendance automatically creates an 'event' contact log entry for each checked donor.",
          "Event contacts earn points per the Scoring settings (default: same as event attendance point value).",
          "If you uncheck a donor who was previously marked present, their contact log entry is removed and their score updates accordingly.",
          "Deleting an event removes all associated contact log entries for every attendee.",
        ],
      },
    ],
    tip: "Log attendance soon after the event while it's fresh. Contact log entries are date-stamped to the event date, not the day you enter them.",
  },

  // ──────────────────────────────────────
  // SETTINGS VIEW
  // ──────────────────────────────────────
  settings: {
    title: "Settings",
    intro:
      "Organization configuration across eight tabs — themes, custom fields, scoring rules, user accounts, and data management.",
    sections: [
      {
        heading: "Appearance",
        items: [
          "Color Theme — choose from 5 palettes. Your selection applies to every view instantly.",
          "Toolbar Style — controls how Zone 2 navigation buttons look: Icon-Dot, Solid, or Pill.",
        ],
      },
      {
        heading: "Fields",
        items: [
          "Donor Pathways — add or rename engagement pathways. Pathways with donors assigned cannot be deleted.",
          "Custom Fields — create organization-specific fields (text, dropdown, checkbox, date, number) that appear on every donor's detail record.",
        ],
      },
      {
        heading: "Scoring",
        items: [
          "Adjust point values for each contact type: Quick Contacts (email, phone, text), Meetings (coffee, lunch, other), and Commitment Signals (donation, volunteering, event attendance).",
          "Set pathway thresholds — the score ranges that define New Donor, Donor of Record, Engagement, and Advocate.",
          "Adjust the decay period (default 24 months) and score cap (default 150 pts).",
          "Use Recalculate All Scores to apply changed rules to every donor — the results panel shows exactly which donors moved and what their new score is. Prospects are automatically skipped.",
        ],
      },
      {
        heading: "Organization",
        items: [
          "Configure your organization's legal identity, mailing address, receipt numbering, and signature block — all the fields that appear on IRS-compliant gift receipts.",
          "A Receipt Readiness indicator at the top shows how many of the 5 required fields are filled. Receipts cannot be generated until all required fields are complete.",
          "All organization settings auto-save to Supabase as you type — no save button needed.",
          "For detailed help on every field, click the ? button while on the Organization tab.",
        ],
      },
      {
        heading: "Personas",
        items: [
          "Manage the persona types available for donor classification. Each persona carries cultivation guidance that appears in the donor's detail record.",
        ],
      },
      {
        heading: "Users (Admin only)",
        items: [
          "Invite new users by entering their full name, email, and role (Director or Admin). They receive an email to set their password.",
          "Three roles: Admin (full access), Director (manages donor portfolios, no Settings access), Event Volunteer (events only).",
          "Use Reset to send a password reset email to any user. Use Delete to revoke access immediately — their assigned donors become unassigned.",
        ],
      },
      {
        heading: "Data",
        items: [
          "Download Backup exports all donors, settings, organization configuration, events, and attendance to a JSON file.",
          "Restore loads a previous backup — this replaces all current data in Supabase.",
          "Clear All permanently deletes all donor records. Always download a backup first.",
        ],
      },
    ],
    tip: "Run a backup before any major import, scoring change, or restore operation. It takes five seconds and protects months of data.",
  },

  // ──────────────────────────────────────
  // ORGANIZATION SETTINGS (shown when Organization tab is active)
  // ──────────────────────────────────────
  organization: {
    title: "Organization Configuration",
    intro:
      "These settings define your organization's identity for IRS-compliant gift receipts, acknowledgment letters, and official correspondence. All fields auto-save to Supabase as you type.",
    reference: [
      {
        heading: "Receipt Readiness",
        items: [
          "The indicator at the top of the page shows how many of the 5 required fields are complete: Legal Name, Address Line 1, City/State/ZIP, EIN, and Receipt Number Prefix.",
          "Receipts cannot be generated until all 5 required fields are filled in. The indicator turns green with a checkmark when you're ready.",
          "If any required fields are missing, the indicator lists them by name so you know exactly what to fill in.",
        ],
      },
      {
        heading: "Organization Identity",
        items: [
          "Display Name is the informal name shown in the app header and footer (e.g. 'PCPA'). This is what users see in every view.",
          "Legal Name is the full legal name exactly as it should appear on IRS receipts and tax documents (e.g. 'Pacific Conservatory Theatre, Inc.'). This must match your IRS filing.",
          "EIN is your Employer Identification Number in XX-XXXXXXX format. It appears in both the receipt header and the small-print footer for IRS compliance.",
        ],
      },
      {
        heading: "Mailing Address",
        items: [
          "Address Line 1 is your primary street or P.O. Box address. It appears in the receipt header alongside the organization name.",
          "Address Line 2 is optional — use it for suite numbers, building names, or 'c/o' lines.",
          "City, State ZIP should be formatted as a single line (e.g. 'San Luis Obispo, CA 93406').",
          "Phone and Email are optional but recommended — they give donors a way to contact the organization if they have questions about their receipt.",
        ],
      },
      {
        heading: "Receipt Configuration",
        items: [
          "Receipt Number Prefix is combined with the year and a sequential number to create unique receipt IDs. For example, if your prefix is 'PCPA', receipts will be numbered PCPA-2026-0001, PCPA-2026-0002, and so on.",
          "The sequence resets each calendar year. Once a receipt number is assigned, it never changes even if the gift is edited.",
          "Fiscal Year Note is optional small-print text that appears in the receipt footer. Use it if you need to clarify your tax year or add a legal disclaimer.",
        ],
      },
      {
        heading: "Signature Block",
        items: [
          "Signatory Name and Title appear on every receipt below the signature line (e.g. 'Jane Smith, Executive Director').",
          "Logo URL should be a public Supabase Storage URL pointing to your organization's logo image. The logo appears in the receipt header next to the organization name. To upload: go to your Supabase project → Storage → create a 'chapter-assets' bucket → upload the image → copy the public URL.",
          "Signature Image URL works the same way — upload a scanned signature image to Supabase Storage and paste the public URL here. If no image is provided, the receipt prints a blank signature line instead.",
          "Recommended image formats: PNG or JPG. Keep logo width under 300px and signature width around 200px for best print quality.",
        ],
      },
      {
        heading: "How These Fields Are Used",
        items: [
          "GiftReceiptTemplate reads every field from these settings to render IRS-compliant receipts for all four gift types: Cash/Check/CC, In-Kind, Stock/Securities, and Donor Advised Fund.",
          "The Email Receipt action in the receipt modal uses the signatory name in the email body and the organization email as the reply-to address.",
          "Backup and restore include all organization settings — restoring a backup will overwrite these values with whatever was saved in the backup file.",
        ],
      },
    ],
    tip: "Fill in all 5 required fields before your first gift entry. You can always update the optional fields (logo, signature image) later — receipts will use a text signature line until an image is uploaded.",
  },

  // ──────────────────────────────────────
  // GIFTS VIEW
  // ──────────────────────────────────────
  gifts: {
    title: "Gifts & Receipting",
    intro:
      "Manage all donor gifts across the organization. Record gifts, generate IRS-compliant receipts, and track acknowledgment status.",
    sections: [
      {
        heading: "Finding Gifts",
        items: [
          "Use the filter bar to narrow gifts by donor name, gift type, fund, acknowledgment status, or date range.",
          "Click any column header to sort the table by that field.",
          "The Clear Filters button resets all filters back to their defaults.",
        ],
      },
      {
        heading: "Recording Gifts",
        items: [
          "Click New Gift to open the gift entry form. Select a donor, enter the amount, date, gift type, fund, and payment method.",
          "Four gift types are supported: Cash/Check/CC, In-Kind, Stock/Securities, and Donor Advised Fund (DAF).",
          "Each type shows the appropriate fields — In-Kind gifts include a fair market value field for the goods and services offset.",
          "You can also record gifts from a donor's detail record using the Record Gift button in the sidebar.",
        ],
      },
      {
        heading: "Receipts & Acknowledgment",
        items: [
          "Click the Receipt button on any gift row to open the receipt modal with a live preview.",
          "Print / Save PDF opens your browser's print dialog — only the receipt prints, not the surrounding interface.",
          "Email Receipt sends a pre-filled email to the donor with the receipt details.",
          "Mark Acknowledged updates the gift's status to show it has been receipted. The button turns green and locks once marked.",
          "Use the checkbox column to select multiple gifts, then Bulk Acknowledge to mark them all at once.",
        ],
      },
      {
        heading: "Exporting",
        items: [
          "Export CSV downloads all currently visible (filtered) gifts as a spreadsheet file.",
          "All three dollar figures are included: gross amount, goods and services FMV, and net deductible.",
        ],
      },
    ],
    tip: "After a fundraising event, use the fund and date filters to isolate that event's gifts, then bulk acknowledge them in one pass.",
  },

  permissions: {
    title: "Permission Management",
    intro:
      "Control what each role can see and do in ClearView. Admin always has full access and cannot be restricted.",
    sections: [
      {
        heading: "Role Templates",
        items: [
          "ClearView includes five predefined role templates: Admin, Director, Event Manager, Gift Processor, and Data Analyst.",
          "Each template comes with a sensible set of default permissions designed for that type of user.",
          "The cards at the top show each role's description, database tier, and how many users are currently assigned to it.",
        ],
      },
      {
        heading: "Database Tiers",
        items: [
          "Every role maps to one of three database tiers: admin, director, or volunteer. This controls server-level data access that the app cannot override.",
          "Director-tier roles (Director) can query gift and fund data. Volunteer-tier roles (Event Volunteer, Read Only) are blocked from these tables at the database level.",
          "If you enable Gifts for a volunteer-tier role in the grid, users will see the UI elements but the data queries will return empty. To grant real gift access, assign the user a director-tier role.",
        ],
      },
      {
        heading: "Permission Grid",
        items: [
          "The grid shows all 12 modules organized in five groups. Each checkbox toggles that module for that role.",
          "Changes save automatically and take effect when affected users next open the app.",
          "When you change a toggle, the role's template card shows a 'customized' badge so you can tell it's been modified from the default.",
        ],
      },
      {
        heading: "Warnings",
        items: [
          "Amber warnings appear when permissions have logical dependencies — for example, enabling Edit Donors without View Donor Detail means users can't reach the edit screen.",
          "A special warning appears if Gifts is enabled for a volunteer-tier role, since the database will block those queries regardless of the UI setting.",
          "These are informational — the system allows the configuration but wants you to know about potential issues.",
        ],
      },
      {
        heading: "Resetting",
        items: [
          "Individual roles can be reset to their template defaults using the Reset button that appears when a role is customized.",
          "Reset All to Defaults restores every role to its original template configuration.",
          "Permissions are included in backups and restored when you import a backup file.",
        ],
      },
    ],
    tip: "Start with the default templates — they match industry best practices for nonprofit organization management. Only customize when you have a specific need.",
  },

  // ──────────────────────────────────────
  // CAMPAIGNS
  // ──────────────────────────────────────
  campaigns: {
    title: "Campaigns",
    intro:
      "Track direct mail campaigns and donor solicitation efforts. Create campaigns, assign donors, and monitor response rates.",
    sections: [
      {
        heading: "Managing Campaigns",
        items: [
          "Create a new campaign with a name, description, target audience, and date range.",
          "Assign donors to a campaign manually or by building a list from the Cultivation Comms tab.",
          "Track campaign responses — gifts that come in during the campaign window are automatically associated.",
        ],
      },
      {
        heading: "Campaign Performance",
        items: [
          "Each campaign card shows total solicited, total responded, and response rate.",
          "Click into a campaign to see the full donor list with individual response status.",
          "Use campaign data to refine future solicitation strategies and identify your most responsive donor segments.",
        ],
      },
      {
        heading: "Donor Welcome Journey — Overview",
        items: [
          "The Welcome Journey tracks new donors from first contact through to Donor of Record status. It uses four stages: Imported → Card Sent → First Gift → Donor of Record.",
          "The goal: every new donor receives a personal welcome card, makes a second gift, and graduates into your regular cultivation pipeline.",
        ],
      },
      {
        heading: "Welcome Journey — Step-by-Step Workflow",
        items: [
          "Step 1 — Create a Campaign: Click 'New Campaign' and name it (e.g., 'Welcome Journey Q2 2026'). Set status to Active.",
          "Step 2 — Import Donors: Click 'Import Prospects' to upload a Spektrix CSV. New donors are created as Prospects and added to the campaign at the 'Imported' stage.",
          "Step 3 — Mail Welcome Cards: Print and mail personal welcome cards to each imported donor. When cards are in the mail, click 'Mark Card Sent' — this advances all Imported recipients to 'Card Sent' and logs a Card contact on each donor's record.",
          "Step 4 — Record First Gifts: When a donor responds, enter their gift in Gifts and select this campaign from the Campaign dropdown. The system automatically advances them to 'First Gift'.",
          "Step 5 — Secure Second Gift: Continue cultivation. When you record a second gift linked to the campaign, the donor automatically advances to 'Donor of Record'.",
          "Step 6 — Graduate: Donors of Record (2+ gifts) are ready to be included in your regular campaigns and cultivation pipeline. Their pathway will advance from Prospect based on their engagement score.",
        ],
      },
      {
        heading: "Welcome Journey — Tips",
        items: [
          "Use the stage filter pills to focus on one group at a time — e.g., filter to 'Card Sent' to see who's waiting for a first gift.",
          "Click a segment in the progress bar for quick filtering.",
          "The advance arrow (>) on each row lets you manually move a single donor forward if needed.",
          "Important: gifts must be linked to the campaign (select it in the Campaign dropdown during gift entry) for auto-advancement to work.",
          "You can run multiple Welcome Journey campaigns concurrently — one per quarter or per Spektrix import batch.",
        ],
      },
    ],
    tip: "Name campaigns consistently (e.g., 'Spring 2026 Annual Fund') so you can compare year-over-year performance in reports.",
  },

  // ──────────────────────────────────────
  // SCHOLARSHIPS
  // ──────────────────────────────────────
  scholarships: {
    title: "Scholarships",
    intro:
      "Manage named scholarship funds, track awards, and report on scholarship giving and distribution.",
    sections: [
      {
        heading: "Scholarship Funds",
        items: [
          "The Scholarships tab lists all named scholarship funds with their current balance and award history.",
          "Create a new fund with a name, description, and optional minimum balance threshold.",
          "Gifts designated to a scholarship fund automatically update its balance.",
        ],
      },
      {
        heading: "Awards",
        items: [
          "The Awards tab tracks individual scholarship awards — who received them, the amount, and the academic period.",
          "Awards draw down the fund balance and are reflected in the fund's award history.",
        ],
      },
      {
        heading: "Reporting",
        items: [
          "The Report tab provides a summary of all scholarship funds, total giving, total awarded, and remaining balances.",
          "Use this to prepare board reports and donor stewardship communications about scholarship impact.",
        ],
      },
    ],
    tip: "Keep scholarship fund names aligned with how donors know them — this makes stewardship reporting easier and more personal.",
  },

  // ──────────────────────────────────────
  // PLAYBOOK
  // ──────────────────────────────────────
  playbook: {
    title: "Playbook",
    intro:
      "Your strategic guide for building revenue-generating relationships. Frameworks, diagnostics, and best practices for fundraising professionals.",
    sections: [
      {
        heading: "Using the Playbook",
        items: [
          "The Playbook is organized into strategic sections — each one covers a key aspect of donor relationship building.",
          "Use the tabs to navigate between frameworks. Each section includes actionable guidance you can apply immediately.",
          "The Playbook connects to your CRM data — links throughout will take you directly to relevant donor records and cultivation tools.",
        ],
      },
      {
        heading: "Getting the Most Value",
        items: [
          "Review the Playbook before cultivation planning sessions to align your team on strategy.",
          "Use the diagnostic tools to assess where your organization stands and identify growth opportunities.",
          "Return to the Playbook periodically — as your donor base evolves, different sections become more relevant.",
        ],
      },
    ],
    tip: "The Playbook is a living resource. Combine its frameworks with your portfolio data and cultivation pipeline for a complete development strategy.",
  },

  // ──────────────────────────────────────
  // SHOWS & SPONSORSHIPS
  // ──────────────────────────────────────
  shows: {
    title: "Shows & Sponsorships",
    intro:
      "Manage productions, sponsorship packages, and benefit fulfillment for show sponsors.",
    sections: [
      {
        heading: "Shows",
        items: [
          "Shows tab: Manage your production catalog. Add each show before selling sponsorships for it.",
          "Shows have a status lifecycle: Upcoming → Active → Closed → Archived.",
          "Archived shows are hidden by default but can be toggled visible.",
        ],
      },
      {
        heading: "Packages",
        items: [
          "Packages tab: Define your sponsor tiers and their benefit lists. Pick benefits from your catalog or add custom benefits unique to a package.",
          "Global packages apply to all shows. Show-specific packages apply to one production only.",
          "Editing a package does not retroactively update existing sponsorship fulfillment rows.",
        ],
      },
      {
        heading: "Sponsorships",
        items: [
          "Sponsorships tab: Track benefit fulfillment for every sponsor. Expand a card to check off benefits as they are delivered.",
          "Board members can view sponsorships but cannot update fulfillment status.",
          "When entering a gift, a Show Sponsorship panel appears automatically if the amount qualifies for any active package.",
        ],
      },
      {
        heading: "Benefit Templates",
        items: [
          "Benefit Templates tab: Your master benefit library. Add every named benefit once here, then pick from this list when building packages.",
          "Editing a template name does not affect existing packages or fulfillments — names are captured at the time a package is built.",
          "Deactivate a template to hide it from the picker without deleting it. Existing packages are unaffected.",
          "Drag to reorder templates. Categories help organize large benefit lists.",
        ],
      },
    ],
    tip: "Build your benefit catalog first, then create packages that pull from it. This keeps benefit names consistent across all your sponsorship tiers.",
  },

  tasks: {
    title: "Tasks",
    intro:
      "The task manager keeps your stewardship cadence running even when you're not in the CRM every day. Tasks can be one-off (a single follow-up) or recurring (a weekly board-chair check-in, a monthly pipeline review). The TasksView (/tasks) is the full editor; the drawer and My Day sidebar are quick-glance surfaces.",
    workflows: [
      {
        id: "create-recurring-cadence",
        title: "Create a recurring stewardship cadence",
        goal: "Turn a rhythm you keep in your head (weekly 1:1, monthly pipeline review, quarterly top-donor stewardship) into a series the system regenerates for you.",
        timeEstimate: "2 min",
        steps: [
          {
            text: "Open Tasks (Ctrl+K then /tasks, or click the Tasks badge in the toolbar).",
            cta: { kind: "nav", view: "tasks", label: "Open Tasks" },
            kbd: ["Ctrl", "K", "/tasks"],
          },
          { text: "Click 'New Task.'" },
          {
            text: "Fill in the title, category, priority, and due date for the FIRST occurrence. The due date is the anchor the system uses to schedule the next one.",
          },
          {
            text: "Under 'Repeats,' pick the frequency: Daily, Weekly, Monthly, Quarterly, Yearly, or Custom (every N days).",
          },
          {
            text: "Under 'Ends,' choose how the series wraps: Never, After N occurrences, or On a specific date. Pilots and campaigns usually want a count or date; standing rituals usually want Never.",
          },
          {
            text: "Save. The first instance appears in your list with a repeating-arrows icon next to the title.",
          },
        ],
        done: "A task with a repeating-arrows icon appears in your list. When you complete it, the next occurrence is automatically generated on the correct date.",
      },
      {
        id: "edit-occurrence-vs-series",
        title: "Edit a single occurrence vs. the whole series",
        goal: "Fix a typo, change a due date, or rescope a recurring task — and know what the change actually affects.",
        timeEstimate: "1 min",
        steps: [
          {
            text: "Open the task from TasksView or the drawer. A banner at the top shows 'Part of a repeating series.'",
          },
          {
            text: "By default, edits apply ONLY to the occurrence you opened — past and future rows stay as-is. Use this for one-off adjustments like pushing THIS week's due date.",
          },
          {
            text: "To change the template (future spawns), click 'Edit series' in the top-right of the banner. This is admin-only. The modal switches to series-edit mode; the due date field disappears because each instance computes its own.",
          },
          {
            text: "Save. Future occurrences inherit the new title, notes, category, priority, frequency, and end rule. Current and past rows are untouched.",
          },
        ],
        done: "You understand which rows your change affected: the open banner confirms 'Editing future occurrences' in series mode, or 'affects only this occurrence' in the default mode.",
      },
      {
        id: "end-series-early",
        title: "End a recurring series early",
        goal: "Stop a recurring task from spawning any more occurrences without having to dismiss each one individually.",
        timeEstimate: "30 sec",
        steps: [
          {
            text: "Find an open instance of the series in TasksView or the drawer.",
          },
          {
            text: "Click the trash icon on that row. This deletes the instance AND deactivates the series — next completions won't spawn a successor.",
          },
          {
            text: "Alternatively (admin): open the task, click 'Edit series,' and change 'Ends' to 'On a specific date' set to yesterday. Future spawns stop at the next completion.",
          },
        ],
        done: "Completing any remaining open instance of the series no longer generates a replacement.",
      },
      {
        id: "find-old-completed-task",
        title: "Find a task I completed months ago",
        goal: "Audit stewardship history or build a board report by seeing every completed task beyond the last 30 days.",
        timeEstimate: "1 min",
        steps: [
          {
            text: "Open the Tasks view.",
            cta: { kind: "nav", view: "tasks", label: "Open Tasks" },
          },
          {
            text: "In the Zone 2 filter bar, click 'Show archive.' The button flips to 'Archive: On' and the list reloads with every completed task regardless of age.",
          },
          {
            text: "Set the Status filter to 'Completed' (or 'All') and optionally filter by category or director to narrow the timeframe.",
          },
          {
            text: "When you're done, click the Archive button again to toggle off. Other task surfaces (drawer, My Day sidebar) are unaffected.",
          },
        ],
        done: "You can see completed tasks older than 30 days in TasksView, and toggling back to the capped view keeps the drawer and My Day fast.",
      },
    ],
    tip: "Recurring tasks spawn ONLY on completion — not on a schedule — so if you're behind, the cadence doesn't snowball into a cascade of overdue rows. Complete when you actually finish the work.",
  },
};
